import { createRouter, createWebHistory } from "vue-router";
import HomeView from "../views/HomeView.vue";
import { t, onLocaleChange } from "../i18n";

const SITE_ORIGIN = "https://www.tamildata.org";

/**
 * Routes carry translation keys rather than literal strings, so the document
 * title and meta description follow the active locale.
 */
const routes = [
  {
    path: "/",
    name: "home",
    component: HomeView,
    meta: { i18nKey: "meta.home" },
  },
  {
    path: "/about",
    name: "about",
    // route level code-splitting: a separate chunk, lazy-loaded on visit.
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/AboutView.vue"),
    meta: { i18nKey: "meta.about" },
  },
  {
    path: "/tamil-data-decentralization",
    name: "tamil-data-decentralization",
    component: () =>
      import(
        /* webpackChunkName: "decentralization" */ "../views/TamilDataDecentralizationView.vue"
      ),
    meta: { i18nKey: "meta.decentralization" },
  },
  {
    path: "/tamil-language-research-fund",
    name: "tamil-language-research-fund",
    component: () =>
      import(
        /* webpackChunkName: "research-fund" */ "../views/TamilLanguageResearchFundView.vue"
      ),
    meta: { i18nKey: "meta.fund" },
  },
  {
    path: "/reward-for-tamil-kids-name",
    name: "reward-for-tamil-kids-name",
    component: () =>
      import(
        /* webpackChunkName: "reward-name" */ "../views/RewardForTamilKidsNameView.vue"
      ),
    meta: { i18nKey: "meta.rewardName" },
  },
  {
    path: "/fourth-child-support",
    name: "fourth-child-support",
    component: () =>
      import(
        /* webpackChunkName: "fourth-child" */ "../views/FourthChildSupportView.vue"
      ),
    meta: { i18nKey: "meta.fourthChild" },
  },
  // Legacy paths kept so existing links and search results keep working.
  {
    path: "/how-it-works",
    redirect: { name: "tamil-data-decentralization" },
  },
  // Workshops now live in the sidebar of the decentralization page.
  {
    path: "/workshop",
    redirect: { name: "tamil-data-decentralization" },
  },
  {
    path: "/join-us",
    name: "join-us",
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/JoinUsView.vue"),
    meta: { i18nKey: "meta.joinUs" },
  },
  {
    path: "/team",
    name: "team",
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/TeamView.vue"),
    meta: { i18nKey: "meta.team" },
  },
  {
    path: "/archive",
    name: "archive",
    component: () => import("../views/ArchiveView.vue"),
    meta: { i18nKey: "meta.archive" },
  },
  {
    path: "/faq",
    name: "faq",
    component: () =>
      import(/* webpackChunkName: "faq" */ "../views/FAQView.vue"),
    meta: { i18nKey: "meta.faq" },
  },
  {
    path: "/contact",
    name: "contact",
    component: () =>
      import(/* webpackChunkName: "contact" */ "../views/ContactView.vue"),
    meta: { i18nKey: "meta.contact" },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to) {
    if (to.hash) {
      return { el: to.hash, behavior: "smooth" };
    }
    return { top: 0 };
  },
});

// ---------------------------------------------------------------------------
// Head management
// ---------------------------------------------------------------------------

function setMetaTag(selector, attribute, name, content) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attribute, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Keeps the document head in sync with the active route and locale.
 *
 * The static tags in public/index.html cover crawlers that never run
 * JavaScript. This covers everything that does render the page — including AI
 * browsing agents — so each route reports its own localized title,
 * description and canonical URL instead of the homepage's English defaults.
 */
function applyRouteMeta(route) {
  if (!route || !route.name) return;

  const siteName = t("common.organization");
  const key = route.meta && route.meta.i18nKey;

  const rawTitle = key ? t(`${key}.title`) : siteName;
  const description = key ? t(`${key}.description`) : "";

  const title = rawTitle.includes(siteName)
    ? rawTitle
    : `${rawTitle} | ${siteName}`;
  const url = `${SITE_ORIGIN}${route.path}`;

  document.title = title;

  if (description) {
    setMetaTag('meta[name="description"]', "name", "description", description);
    setMetaTag(
      'meta[property="og:description"]',
      "property",
      "og:description",
      description
    );
    setMetaTag(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      description
    );
  }

  setMetaTag('meta[property="og:title"]', "property", "og:title", title);
  setMetaTag('meta[name="twitter:title"]', "name", "twitter:title", title);
  setMetaTag('meta[property="og:url"]', "property", "og:url", url);

  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", url);
}

router.afterEach((to) => applyRouteMeta(to));

// Re-apply on language change so the tab title follows the switcher.
onLocaleChange(() => applyRouteMeta(router.currentRoute.value));

export default router;
